'use client';

// components/search/ProductGrid/PriceDisplay.tsx
// Shared price renderer for the product cards. Handles the loading skeleton, the
// external-pricing states (customer price / on-sale / unavailable) and the RRP
// fallback. Used by both ProductCard (standard) and SyfakProductCard (B2B).

import type { NormalizedPrice } from '@/lib/pricing/externalPricing';

export interface PriceProps {
  layout: 'horizontal' | 'stacked';
  loading: boolean;
  customerPrice: NormalizedPrice | null | undefined;
  rrp: number | null | undefined;
  rrpOriginal: number | null | undefined;
  unavailableLabel: string;
  /**
   * Price unit for products sold by weight («κιλό» → «10,95€ /κιλό», as bazaar's
   * own page shows it). Omitted → the price renders exactly as before.
   */
  perUnit?: string;
}

/**
 * Whether this card will render the "no price for you" state rather than a
 * price — i.e. the endpoint answered `[false,false]`, or returned an object
 * with neither a regular nor a sale price.
 *
 * Exported so a card can hide its price HEADING («ΤΙΜΗ ΡΟΗΣ») in exactly the
 * cases this renders the unavailable label, instead of re-deriving the rule and
 * drifting from it. A fetch still in flight is NOT "unavailable" — the skeleton
 * keeps its heading so the row does not jump when the price arrives.
 */
export function isPriceUnavailable(
  loading: boolean,
  customerPrice: NormalizedPrice | null | undefined,
): boolean {
  if (loading) return false;
  if (customerPrice === null) return true;
  if (customerPrice) return !customerPrice.sale && !customerPrice.regular;
  return false; // undefined → the RRP fallback path, which does show a price
}

export function PriceDisplay({ layout, loading, customerPrice, rrp, rrpOriginal, unavailableLabel, perUnit }: PriceProps) {
  // Skeleton — fetch in flight.
  if (loading) {
    return (
      <div
        className="bg-gray-200 animate-pulse rounded"
        style={{ width: 64, height: 14 }}
        aria-label="loading price"
      />
    );
  }

  // External-pricing endpoint returned `[false, false]` → no price available.
  if (customerPrice === null) {
    return (
      <span className="text-[12px] font-gotham font-medium text-gray-500 leading-normal">
        {unavailableLabel}
      </span>
    );
  }

  // Customer-specific price provided. Use the formatted strings as-is.
  if (customerPrice) {
    const wrapClass = layout === 'stacked' ? 'flex flex-col items-start gap-[2px]' : 'flex items-center gap-[6px] h-[25px]';
    if (customerPrice.sale) {
      return (
        <div className={wrapClass}>
          <span className="text-[12px] font-gotham font-bold text-brand leading-normal">{customerPrice.sale}</span>
          {customerPrice.regular && (
            <span className="text-[10px] font-gotham font-normal text-black line-through leading-normal">{customerPrice.regular}</span>
          )}
        </div>
      );
    }
    if (customerPrice.regular) {
      return <span className="text-[12px] font-gotham font-medium text-black leading-normal">{customerPrice.regular}</span>;
    }
    // Both null but object exists — treat as unavailable.
    return (
      <span className="text-[12px] font-gotham font-medium text-gray-500 leading-normal">
        {unavailableLabel}
      </span>
    );
  }

  // Default: RRP from product (existing parousiasi/kaizershop path).
  const wrapClass = layout === 'stacked' ? 'flex flex-col items-start gap-[2px]' : 'flex items-center gap-[6px] h-[25px]';
  return (
    <div className={wrapClass}>
      <span className="text-[12px] font-gotham font-medium text-black leading-normal">
        {rrp?.toFixed(2)}&euro;
        {perUnit && <span className="text-[10px] font-normal text-gray-500"> /{perUnit}</span>}
      </span>
      {rrpOriginal && rrpOriginal > (rrp || 0) && (
        <span className="text-[10px] font-gotham font-normal text-black line-through leading-normal">
          {rrpOriginal.toFixed(2)}&euro;
        </span>
      )}
    </div>
  );
}
